import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from '@/components/ui/use-toast';
import { INITIAL_EDIT_EVENT, MARKETS_PAGE_SIZE, ROUTES } from '@/constants';
import { asApiError } from '@/utils';
import {
	useGetBotTradesQuery,
	useGetSignedLinkToDownloadFileQuery,
	useGetSlatesByEventIdQuery,
	useGetMarketCategoriesQuery,
	useGetMarketsListQuery,
	useGetLobbiesByEventIdQuery,
	useGetWaitlistsQuery,
	useGetEventQuery,
	useUpdateEventMutation,
	useDeleteEventMutation,
	useLazyGetLobbiesByEventIdForAdminQuery,
	useLazyGetMarketsListQuery,
} from '@/store/api';
import { marketsApi } from '@/store/api/markets.api';
import { useAppDispatch } from '@/store';
import { useEventTrades, useEventChat } from '@/hooks';
import { useEventImageUpload } from '@/hooks/upload/use-event-image-upload.hook';
import { useEventMarketCrud } from './use-event-market-crud.hook';
import { useEventRealtimeSync } from './use-event-realtime-sync.hook';
import { SocketContext } from '../../contexts/socket.context';
import {
	EventStatus,
	LobbyTypeEnum,
	EventType,
	MarketSortField,
	MarketStatus,
	SortDirection,
} from '@sportspredict/shared';

export function useEventManagementPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { UploadImage, compressedImgData } = useEventImageUpload();
	const [isImgDeleted, setIsImgDeleted] = useState(false);

	const eventId = searchParams.get('id');
	const [event, setEvent] = useState<any>(null);
	const [isUploading, setIsUploading] = useState(false);

	const isProbability = event?.type === EventType.Probability;

	const socket = useContext(SocketContext);

	// Edit Event Modal States
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editedEvent, setEditedEvent] = useState<any>(INITIAL_EDIT_EVENT);

	// Add new states for lobbies
	const [lobbies, setLobbies] = useState<any[]>([]);
	const [getLobbiesByEventIdForAdmin] = useLazyGetLobbiesByEventIdForAdminQuery();

	const {
		data: eventQueryData,
		refetch: refetchEvent,
		isLoading,
		error,
	} = useGetEventQuery(eventId ?? skipToken, { skip: !eventId });
	const [updateEvent] = useUpdateEventMutation();
	const [deleteEvent] = useDeleteEventMutation();

	const { data: lobbiesQueryData, refetch: refetchLobbies } = useGetLobbiesByEventIdQuery(
		eventId ?? skipToken
	);

	useEffect(() => {
		if (!lobbiesQueryData) return;
		setLobbies((prev: any[]) =>
			lobbiesQueryData.map((lobby: any) => {
				const prevLobby = prev.find((p: any) => p?.id === lobby.id);
				return lobby.access_password === undefined && prevLobby?.access_password !== undefined
					? { ...lobby, access_password: prevLobby.access_password }
					: lobby;
			})
		);
		setSelectedChatLobby((prev: string) => prev || lobbiesQueryData[0]?.id || '');
	}, [lobbiesQueryData]);

	useEffect(() => {
		if (!eventId) return;
		let cancelled = false;
		getLobbiesByEventIdForAdmin(eventId)
			.unwrap()
			.then((adminLobbies: any[]) => {
				if (!cancelled) setLobbies(adminLobbies);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [eventId]);

	useEffect(() => {
		if (!eventQueryData) return;
		setPreviewUrl(eventQueryData?.img_variants?.s);
		setEvent(eventQueryData);
		setEditedEvent({
			title: eventQueryData.title || '',
			description: eventQueryData.description || '',
			category_id: eventQueryData.category_id || '',
			status: eventQueryData.status || EventStatus.Upcoming,
			displayed_start_time: eventQueryData.displayed_start_time || '',
			end_date: eventQueryData.end_date || '',
			closing_date: eventQueryData.closing_date || '',
			is_private: eventQueryData.is_private || false,
			access_password: eventQueryData.access_password || '',
			markets_open_time: eventQueryData.markets_open_time || '',
			has_multiple_lobbies: eventQueryData.has_multiple_lobbies || false,
			is_odds_locked: eventQueryData.is_odds_locked || false,
			type: eventQueryData.type || EventType.Binary,
		});
	}, [eventQueryData]);

	// Get current active tab
	const [activeTab, setActiveTab] = useState('slatesMatches');

	// Futures Markets state (managed here so the socket listener lives at this level)
	const [availableFutures, setAvailableFutures] = useState<any[]>([]);
	const [futuresMarkets, setFuturesMarkets] = useState<any[]>([]);
	const [isLoadingFutures, setIsLoadingFutures] = useState(false);

	// Add timer setting states
	const [isSettingTimer, setIsSettingTimer] = useState(false);
	const [timerSettings, setTimerSettings] = useState('');

	const [exportTradesJobId, setExportTradesJobId] = useState<any>(null);
	const [exportedTradesFile, setExportedTradesFile] = useState<any>(null);

	const { data: signedLinkData } = useGetSignedLinkToDownloadFileQuery(
		exportedTradesFile ? exportedTradesFile.id : skipToken
	);

	const currentCrowdLobby = useMemo(() => {
		return (lobbies ?? []).find((l: any) => l?.type === LobbyTypeEnum.CrowdLock) ?? null;
	}, [lobbies]);

	const {
		selectedChatLobby,
		setSelectedChatLobby,
		chatTargetType,
		messageList,
		loadMore,
		isFetching,
		hasMore,
		selectedChatUser,
		setSelectedChatUser,
		chatUserMessages,
		chatUserMessagesCount,
		chatUserLobbyInfo,
		setMessages,
		handleDeleteMessage,
		handleTimeoutUser,
		handleSetChatStatus,
		handleSelectedChatUser,
		handleSetChatTargetType,
	} = useEventChat({ socket, eventId, activeTab, setLobbies });

	// Update bulk market state to handle multiple markets
	const [bulkMarkets, setBulkMarkets] = useState([
		{ question: '', context: '', initial_value: 50, isSelectedToCreate: true },
	]);

	const { data: waitlistUsers = [] } = useGetWaitlistsQuery(
		{ event_id: eventId! },
		{ skip: activeTab !== 'notifications' || !eventId }
	);

	const [previewUrl, setPreviewUrl] = useState('');

	const [isDeleting, setIsDeleting] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	const [filterMatchId, setFilterMatchId] = useState<any>(null);
	const [filterSlateId, setFilterSlateId] = useState<any>(null);
	const [filterLobbyId, setFilterLobbyId] = useState<any>(null);
	const [marketStatusFilter, setMarketStatusFilter] = useState<string>('all');
	const [filteredMarketGroups, setFilteredMarketGroups] = useState<string[]>([]);
	const [marketsPage, setMarketsPage] = useState(1);

	const dispatch = useAppDispatch();

	const marketsQueryArgs = useMemo(() => {
		if (!eventId) return null;
		return {
			eventId,
			lobbyId: filterLobbyId && filterLobbyId !== 'all' ? filterLobbyId : undefined,
			slateId: filterSlateId || undefined,
			matchId: filterMatchId || undefined,
			status: marketStatusFilter as MarketStatus | 'all',
			marketGroups: filteredMarketGroups?.length ? filteredMarketGroups : undefined,
			sortBy: MarketSortField.Priority,
			sortDirection: SortDirection.Asc,
			page: marketsPage,
			pageSize: MARKETS_PAGE_SIZE,
		};
	}, [
		eventId,
		filterLobbyId,
		filterSlateId,
		filterMatchId,
		marketStatusFilter,
		filteredMarketGroups,
		marketsPage,
	]);

	const { data: marketsData, refetch: refetchMarkets } = useGetMarketsListQuery(
		marketsQueryArgs ?? skipToken,
		{ refetchOnMountOrArgChange: true }
	);

	const [triggerGetMarkets] = useLazyGetMarketsListQuery();

	const markets = marketsData?.list ?? [];
	const marketsTotal = marketsData?.total ?? 0;

	const setMarkets = useCallback(
		(updater: any) => {
			if (!marketsQueryArgs) return;
			dispatch(
				marketsApi.util.updateQueryData('getMarketsList', marketsQueryArgs, (draft: any) => {
					draft.list = typeof updater === 'function' ? updater(draft.list) : updater;
				})
			);
		},
		[dispatch, marketsQueryArgs]
	);

	const loadMarketData = useCallback(async () => {
		if (!eventId) return;
		await refetchMarkets();
	}, [eventId, refetchMarkets]);

	const fetchAllFilteredMarkets = useCallback(async () => {
		if (!marketsQueryArgs) return [];
		const { page: _page, pageSize: _pageSize, ...baseArgs } = marketsQueryArgs;
		const size = marketsTotal > 0 ? marketsTotal : MARKETS_PAGE_SIZE;
		const result = await triggerGetMarkets({ ...baseArgs, page: 1, pageSize: size }).unwrap();
		return result?.list ?? [];
	}, [marketsQueryArgs, marketsTotal, triggerGetMarkets]);

	useEffect(() => {
		setMarketsPage(1);
	}, [
		eventId,
		filterLobbyId,
		filterSlateId,
		filterMatchId,
		marketStatusFilter,
		filteredMarketGroups,
	]);

	const {
		data: slatesData = [],
		isLoading: slatesIsLoading,
		error: slatesError,
		refetch: slatesRefetch,
	} = useGetSlatesByEventIdQuery(eventId ? { eventId } : skipToken);

	const { data: marketCategories = [] } = useGetMarketCategoriesQuery(event?.category_id, {
		skip: !event?.category_id && !event?.attached_category_games,
	});

	const isActiveTabTrades = activeTab === 'trades';

	const { data: botTrades = [], refetch: refetchBotTrades } = useGetBotTradesQuery(
		{ event_id: eventId! },
		{ skip: activeTab !== 'botTrades' || isProbability || !eventId }
	);

	const {
		selectedLobbyFilter,
		setSelectedLobbyFilter,
		tradeFilterLobbyId,
		tradeStats,
		trades,
		tradePage,
		setTradePage,
		tradeTotal,
		hasLoadedTrades,
		refetchTradesStat,
		refetchTradeList,
		isLoadingTrades,
		scheduleRefetchTradeStat,
		scheduleRefetchTradeList,
		setShouldRefetchTrade,
	} = useEventTrades({ eventId, isProbability, isActiveTabTrades });

	useEffect(() => {
		setTradePage(1);

		if (!isActiveTabTrades && hasLoadedTrades) {
			setShouldRefetchTrade(true);
		}
	}, [hasLoadedTrades, isActiveTabTrades, selectedLobbyFilter]);

	useEventRealtimeSync({
		socket,
		eventId,
		event,
		setEvent,
		setLobbies,
		refetchLobbies,
		isProbability,
		hasLoadedTrades,
		isActiveTabTrades,
		scheduleRefetchTradeStat,
		scheduleRefetchTradeList,
		setShouldRefetchTrade,
		exportTradesJobId,
		setExportTradesJobId,
		exportedTradesFile,
		setExportedTradesFile,
		signedLinkData,
		setAvailableFutures,
		setFuturesMarkets,
		setIsLoadingFutures,
		setBulkMarkets,
		slatesRefetch,
	});

	useEffect(() => {
		if (compressedImgData?.originalId && compressedImgData?.originalUrl) {
			const previewImg = compressedImgData.variants.l || compressedImgData.originalUrl;

			setPreviewUrl(previewImg);
			setIsImgDeleted(false);
		}
	}, [compressedImgData]);

	const loadEventData = async () => {
		if (!eventId) return;
		try {
			await refetchEvent();
			await loadMarketData();
		} catch (err: any) {
			if (err.message?.includes('429')) {
				toast({
					variant: 'destructive',
					title: 'Rate Limit Reached',
					description: 'Please wait a moment before trying again',
				});
			}
			console.error('Error loading event data', err);
		}
	};

	useEffect(() => {
		setLobbies([]);
		setSelectedChatLobby('');
	}, [eventId]);

	useEffect(() => {
		if (event && isEditModalOpen) {
			setEditedEvent({
				title: event.title || '',
				description: event.description || '',
				category_id: event.category_id || '',
				status: event.status || EventStatus.Upcoming,
				displayed_start_time: event.displayed_start_time || '',
				opening_time: event.opening_time || '',
				end_date: event.end_date || '',
				closing_date: event.closing_date || '',
				is_private: event.is_private || false,
				access_password: event.access_password || '',
				markets_open_time: event.markets_open_time || '',
				has_multiple_lobbies: event.has_multiple_lobbies || false,
				is_odds_locked: event.is_odds_locked || false,
				type: event.type || EventType.Binary,
			});
		}
	}, [isEditModalOpen, event]);

	const loadLobbies = async () => {
		if (!eventId) return;

		try {
			const lobbiesData = await getLobbiesByEventIdForAdmin(eventId).unwrap();
			setLobbies(lobbiesData);
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message?.includes('429')
					? 'Too many requests. Please wait a moment.'
					: 'Failed to load lobbies',
			});
			console.error(error);
			throw new Error('Error loading lobbies');
		}
	};

	const handleCreateLobby = () => {
		if (!eventId || !event) return;
		navigate(`${ROUTES.CREATE_LOBBY}?eventId=${eventId}`, {
			state: { event, hasExistingDefault: lobbies?.some((l) => l.is_default) ?? false },
		});
	};

	// Add waitlist loading to the notifications tab handler
	const handleTabChange = (value: string) => {
		setActiveTab(value);
	};

	const handleMatchMarketsManage = useCallback(
		async (matchId: any) => {
			const parentSlate = (slatesData || []).find((s: any) =>
				s.matches.some((m: any) => m.id === matchId)
			);

			if (parentSlate) {
				setFilterSlateId(parentSlate.id);
				setFilterMatchId(matchId);
			} else {
				setFilterSlateId(null);
				setFilterMatchId(null);
			}

			setActiveTab('markets');
		},
		[slatesData]
	);

	const handleEventUpdate = async () => {
		if (isUploading) return;
		try {
			const toISO = (val: any): any =>
				val && !isNaN(new Date(val).getTime()) ? new Date(val).toISOString() : undefined;

			const startDate = toISO(editedEvent.displayed_start_time);
			const endDate = toISO(editedEvent.end_date);
			const closingDate = editedEvent.closing_date ? toISO(editedEvent.closing_date) : null;

			if (startDate > endDate) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Start date cannot be later than end date',
				});
				return;
			}

			if (closingDate && endDate > closingDate) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: 'Real end date cannot be earlier than end date',
				});
				return;
			}

			const payload: any = {
				title: editedEvent.title,
				description: editedEvent.description,
				category_id: editedEvent.category_id,
				status: editedEvent.status,
				is_private: editedEvent.is_private,
				access_password: editedEvent.access_password,
				displayed_start_time: startDate,
				opening_time: editedEvent.opening_time ? toISO(editedEvent.opening_time) : null,
				end_date: endDate,
				closing_date: closingDate,
				markets_open_time: toISO(editedEvent.markets_open_time),
				has_multiple_lobbies: editedEvent.has_multiple_lobbies,
				is_odds_locked: editedEvent.is_odds_locked,
				type: editedEvent.type,
			};

			if (!isImgDeleted && compressedImgData) {
				payload.img_parent_id = compressedImgData?.originalId;
				payload.img_variants = compressedImgData?.variants;
			}

			if (isImgDeleted) {
				payload.img_parent_id = null;
				payload.img_variants = null;
			}

			await updateEvent({ id: eventId!, ...payload }).unwrap();

			setEvent({ ...event, ...payload });
			setIsEditModalOpen(false);
			toast({ title: 'Success', description: 'Event updated successfully' });
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message?.includes('429')
					? 'Too many requests. Please wait a moment.'
					: 'Failed to update event',
			});
			console.error(error);
			throw new Error('Error updating event');
		}
	};

	const handleDeleteEvent = async (idToDelete = eventId) => {
		if (!idToDelete) {
			throw new Error('No event ID provided to delete.');
		}

		setIsDeleting(true);
		try {
			toast({
				title: 'Deleting Event',
				description: `Deleting trades, markets and lobbies…`,
				duration: 5000,
			});

			try {
				await deleteEvent(idToDelete).unwrap();
				toast({
					title: 'Success',
					description: 'Event and all associated data deleted in one go!',
				});
				if (idToDelete === eventId) {
					navigate(ROUTES.ADMIN);
				}
			} catch (e) {
				console.error(e);
				throw new Error(`Failed to delete event ${idToDelete}`);
			}
		} catch (error) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: 'Something went wrong. Check the console for details.',
			});
			console.error(error);
			throw new Error('Unexpected error during deletion process');
		} finally {
			setIsDeleting(false);
			setShowDeleteDialog(false);
		}
	};

	const handleImageUpload = async (e: any) => {
		if (isUploading) return;
		const input = e.target;
		const file = e.target.files[0];
		if (!file) return;
		try {
			setIsUploading(true);
			await UploadImage(file);
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: 'Error',
				description: asApiError(error).message?.includes('429')
					? 'Too many requests. Please wait a moment.'
					: error instanceof Error
						? error.message
						: 'Failed to upload image',
			});
			console.error(error);
		} finally {
			input.value = '';
			setIsUploading(false);
		}
	};

	const {
		isAddingMarket,
		setIsAddingMarket,
		newMarket,
		setNewMarket,
		isBulkCreatingMarkets,
		setIsBulkCreatingMarkets,
		isImportingMarkets,
		setIsImportingMarkets,
		isUploadingFile,
		importedMarkets,
		setImportedMarkets,
		fileUploaded,
		setFileUploaded,
		fileInputRef,
		settlingMarketId,
		settlementProgress,
		bulkSlateId,
		setBulkSlateId,
		bulkMatchId,
		setBulkMatchId,
		bulkLobbyMode,
		setBulkLobbyMode,
		bulkSelectedLobbyIds,
		setBulkSelectedLobbyIds,
		globalClosingDate,
		setGlobalClosingDate,
		globalOpeningDate,
		setGlobalOpeningDate,
		handleCreateMarket,
		handleFileUpload,
		handleImportMarkets,
		addMarketRow,
		removeMarketRow,
		updateMarketRow,
		handleBulkCreateMarkets,
		exportMarketsToCSV,
		exportTradesToCSV,
		handleSettleMarket,
	} = useEventMarketCrud({
		eventId,
		event,
		currentCrowdLobby,
		markets,
		setMarkets,
		loadMarketData,
		fetchAllFilteredMarkets,
		tradeFilterLobbyId,
		setExportTradesJobId,
		setExportedTradesFile,
		bulkMarkets,
		setBulkMarkets,
		marketCategories,
	});

	const participants = new Set();

	lobbies?.forEach((lobby: any) => {
		lobby?.lobby_users?.forEach((lobby_user: any) => {
			if (lobby_user?.user_id) participants.add(lobby_user.user_id);
		});
	});

	const participantsCount = participants.size;

	return {
		navigate,
		eventId,
		isLoading,
		event,
		error,
		isUploading,
		isProbability,
		isEditModalOpen,
		setIsEditModalOpen,
		editedEvent,
		setEditedEvent,
		isAddingMarket,
		setIsAddingMarket,
		newMarket,
		setNewMarket,
		markets,
		setMarkets,
		botTrades,
		lobbies,
		activeTab,
		handleTabChange,
		isBulkCreatingMarkets,
		setIsBulkCreatingMarkets,
		availableFutures,
		setAvailableFutures,
		futuresMarkets,
		setFuturesMarkets,
		isLoadingFutures,
		setIsLoadingFutures,
		isSettingTimer,
		setIsSettingTimer,
		timerSettings,
		setTimerSettings,
		bulkMarkets,
		setBulkMarkets,
		waitlistUsers,
		isImportingMarkets,
		setIsImportingMarkets,
		isUploadingFile,
		importedMarkets,
		setImportedMarkets,
		fileUploaded,
		setFileUploaded,
		fileInputRef,
		previewUrl,
		setPreviewUrl,
		settlingMarketId,
		settlementProgress,
		isDeleting,
		showDeleteDialog,
		setShowDeleteDialog,
		bulkSlateId,
		setBulkSlateId,
		bulkMatchId,
		setBulkMatchId,
		bulkLobbyMode,
		setBulkLobbyMode,
		bulkSelectedLobbyIds,
		setBulkSelectedLobbyIds,
		filterMatchId,
		setFilterMatchId,
		filterSlateId,
		setFilterSlateId,
		filterLobbyId,
		setFilterLobbyId,
		marketStatusFilter,
		setMarketStatusFilter,
		filteredMarketGroups,
		setFilteredMarketGroups,
		marketsPage,
		setMarketsPage,
		marketsTotal,
		slatesData,
		slatesIsLoading,
		slatesError,
		slatesRefetch,
		marketCategories,
		selectedLobbyFilter,
		setSelectedLobbyFilter,
		tradeFilterLobbyId,
		tradeStats,
		trades,
		tradePage,
		setTradePage,
		tradeTotal,
		refetchTradesStat,
		refetchTradeList,
		isLoadingTrades,
		globalClosingDate,
		setGlobalClosingDate,
		globalOpeningDate,
		setGlobalOpeningDate,
		setIsImgDeleted,
		handleCreateLobby,
		handleMatchMarketsManage,
		handleEventUpdate,
		handleDeleteEvent,
		handleCreateMarket,
		handleFileUpload,
		handleImportMarkets,
		handleImageUpload,
		addMarketRow,
		removeMarketRow,
		updateMarketRow,
		handleBulkCreateMarkets,
		exportMarketsToCSV,
		exportTradesToCSV,
		handleSettleMarket,
		loadMarketData,
		loadEventData,
		loadLobbies,
		loadBotTradeData: refetchBotTrades,
		participantsCount,
		selectedChatLobby,
		setSelectedChatLobby,
		chatTargetType,
		messageList,
		loadMore,
		isFetching,
		hasMore,
		selectedChatUser,
		setSelectedChatUser,
		chatUserMessages,
		chatUserMessagesCount,
		chatUserLobbyInfo,
		setMessages,
		handleDeleteMessage,
		handleTimeoutUser,
		handleSetChatStatus,
		handleSelectedChatUser,
		handleSetChatTargetType,
	};
}
